import React, { useState, useRef, useMemo } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import classes from 'classnames';
import { Callout, IconButton, TextField, List, DirectionalHint, FontSizes, FontWeights, mergeStyleSets, mergeStyles } from '@fluentui/react';
import { Key } from 'src/constants/index';

const classNames = mergeStyleSets({
  root: {
    width: '100%',
  },
  label: {
    display: 'inline-block',
    fontWeight: FontWeights.semibold,
    paddingBottom: '5px',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    width: '100%',
  },
  container: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    border: '1px solid black',
    width: '100%',
    selectors: {
      ':focus-within': {
        margin: -1,
        border: '2px solid #507f70',
      }
    }
  },
  textField: {
    flexGrow: '1',
    selectors: {
      '& input': {
        selectors: {
          ':hover::placeholder': {
            color: '#323130',
          },
        },
      },
    },
  },
  errorContainer: {
    border: '1px solid #a4262c',
  },
  errorMessage: {
    margin: 0,
    paddingTop: 5,
    fontSize: FontSizes.small,
    fontWeight: FontWeights.regular,
    color: '#a4262c',
  },
  tooltip: {
    display: 'inline-block',
    fontSize: FontSizes.small,
    fontWeight: FontWeights.semilight,
    marginTop: '4px',
  },
  calloutRoot: {
    minHeight: 1,
    height: '60vh',
    overflowX: 'hidden',
    overflowY: 'auto',
  },
  calloutList: {
  },
  calloutListItem: {
    height: 34,
    lineHeight: 34,
    cursor: 'pointer',
    boxSizing: 'border-box',
    border: '1px solid transparent',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    width: '100%',

    selectors: {
      ':hover': {
        backgroundColor: '#f3f2f1',
      },
      '& > .item__title': {
        margin: '0px 8px 0px 8px',
      },
    }
  },
  calloutListItemSelected: {
    borderColor: 'black',
  },
})





export const TextFieldWithCallout = (props) => {
  const {
    label = '',
    placeholder = 'Start typing to filter',
    query,
    text,
    isDropdownOpened: isOpen,
    errorMessage = '',
    onQueryChanged,
    onKeyDown,
    onOpenDropdown,
    disabled = false,
    component: Component,
    subComponent,
    styles = {},
    textFieldProps = {},
    searchHintText = '',
    tooltip = '',
    invalid = false,
    ...restProps
  } = props;

  const rootRef = useRef();
  const { root: rootStyle, label: labelStyle } = styles;

  const hasError = !!errorMessage;
  const calloutWidth = rootRef.current?.offsetWidth ?? 0;
  const openDropdown = (open) => {
    if (open !== isOpen && open) {
      const ref = rootRef.current?.querySelector('.ms-TextField-field');
      ref?.focus();
      ref?.select();
    }
    onOpenDropdown?.(open);
  }

  function handleDismissCallout() {
    openDropdown(false);
  }

  function handleButtonClick() {
    openDropdown(!isOpen);
  }

  const suffix = useMemo(() =>
    searchHintText && query && query !== text ? searchHintText : undefined,
    [query, text, searchHintText]);

  return (
    <div className={classNames.root} style={rootStyle}>
      { label && <span className={mergeStyles(classNames.label, labelStyle || {})}>{label}</span> }
      <div ref={rootRef} className={classes(classNames.container, { [classNames.errorContainer]: hasError || invalid})}>
        <TextField borderless className={classNames.textField} disabled={disabled}
          placeholder={placeholder} value={query === null ? text : query}
          onChange={(event) => onQueryChanged(event.target.value)}
          onKeyDown={(event) => onKeyDown?.(event)} onFocus={() => openDropdown(true)}
          suffix={suffix}
        />
        <IconButton iconProps={{ iconName: 'ChevronDown' }} tabIndex={-1} disabled={disabled} checked={isOpen} onClick={handleButtonClick} />
      </div>
      { hasError && !isOpen && <p className={classNames.errorMessage}><span>{errorMessage}</span></p> }
      { tooltip && <span className={classNames.tooltip}>{tooltip}</span> }
      { subComponent?.() }

      { isOpen && (
        <Callout
          gapSpace={1}
          alignTargetEdge
          isBeakVisible={false}
          directionalHint={DirectionalHint.bottomCenter}
          directionalHintFixed
          target={rootRef.current}
          onDismiss={handleDismissCallout}
          calloutWidth={calloutWidth}
          calloutMinWidth={calloutWidth}
          calloutMaxWidth={calloutWidth}
          data-is-scrollable
        >
            <Component />
        </Callout>
      )}
    </div>
  )
}


export const SearchableComboBox = ({ text, options, selectedKey, placeholder, disabled, errorMessage, instanceSearch = false, onChange, onSearch, onFilterChange, onRenderCell, ...restProps }) => {
  const listBoxRef = useRef();
  const debouncedSearch = useDebouncedCallback(value => onSearch?.(value), 600);

  const [isOpen, setOpen] = useState(false);
  const [query, setQuery] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const filteredItems = useMemo(() => filterItems(options, query), [options, query]);
  const items = useMemo(() => [...filteredItems], [filteredItems, selectedIndex]);
  const selectedItem = useMemo(() => options.find(item => item.key === selectedKey), [options, selectedKey]);
  const displayName = useMemo(() => selectedItem?.text ?? '', [selectedItem]);
  const tooltip = useMemo(() => selectedItem?.tooltip ?? '', [selectedItem]);
  const subComponent = useMemo(() => selectedItem?.component, [selectedItem]);

  function handleKeyDown(event) {
    const { key } = event;
    switch (key) {
      case Key.Up:
      case Key.Down: {
        event.preventDefault();

        const index = key === Key.Up ? Math.max(selectedIndex - 1, 0) : Math.min(selectedIndex + 1, items.length - 1);
        setSelectedIndex(index);
        openDropdown(true);

        listBoxRef.current?.scrollToIndex(index, () => 34, 'auto');
        break;
      }

      case Key.Escape:
      case Key.Tab: {
        openDropdown(false);
        break;
      }

      case Key.Enter: {
        event.preventDefault();

        if (selectedIndex === -1) {
          onSearch?.(query);
        }
        else {
          onChange(event, items[selectedIndex])
          openDropdown(false);
        }
        break;
      }
    }
  }

  function queryChanged(value) {
    setQuery(value);
    onFilterChange?.(value);
  }

  function handleTextChange(value) {
    if (instanceSearch) { debouncedSearch(value) }

    queryChanged(value);
    openDropdown(true);
  }

  function handleItemClick(event, item) {
    onChange(event, item);
    openDropdown(false);
  }

  function openDropdown(open) {
    if (open !== isOpen) {
      setOpen(open);
      if (query !== null) { queryChanged(null) }
      if (selectedIndex > -1) { setSelectedIndex(-1) }
    }
  }

  const ListComponent = () => {
    return <List items={items} ref={listBoxRef}
      onShouldVirtualize={() => false}
      onRenderCell={!onRenderCell
        ? (item, index) => <ListItem selected={index === selectedIndex} item={item} onClick={handleItemClick} />
        : (item, index) => onRenderCell(item, index === selectedIndex, handleItemClick)
      }
    />
  }

  return (
    <TextFieldWithCallout
      placeholder={placeholder}
      query={query}
      text={text ?? displayName}
      isDropdownOpened={isOpen}
      errorMessage={errorMessage}
      onQueryChanged={handleTextChange}
      onKeyDown={handleKeyDown}
      onOpenDropdown={openDropdown}
      disabled={disabled}
      component={ListComponent}
      subComponent={subComponent}
      tooltip={tooltip}
      {...restProps}
    />
  );
}

const ListItem = ({ item, selected, onClick }) => (
  <div className={`${classNames.calloutListItem} ${selected && classNames.calloutListItemSelected}`}
    onClick={event => onClick(event, item)} data-is-focusable='true'>
    <span className="item__title">{item.text}</span>
  </div>
)

function filterItems(items, search) {
  if (search === null) { return items }

  search = search.toLowerCase();
  return items.filter(item => item.text.toLowerCase().includes(search));
}


export default SearchableComboBox;
